import { useState } from 'react';
import useSizeInputHandlers from 'src/components/TradeForm/SizeInput/hooks/useSizeInputHandlers';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import { INVARIANT_CHECKER_INPUT_IDS } from 'src/utils/UIInvariants/constants';
import useSizeInputSource from 'src/components/TradeForm/SizeInput/hooks/useSizeInputSource';

interface Params {
	maxQuoteAvailable?: number;
	baseSizeStringValue: string;
	quoteSizeStringValue: string;
	onBaseSizeChange: (value: string) => void;
	onQuoteSizeChange: (value: string) => void;
	onInputSourceChange?: (inputSource: 'base' | 'quote') => void;
	formattedMarketSymbol: string;
	stepSizeFromProps: number;
	disabledFromProps?: boolean;
}

export default function useSizeInput({
	maxQuoteAvailable,
	baseSizeStringValue,
	quoteSizeStringValue,
	onBaseSizeChange,
	onQuoteSizeChange,
	onInputSourceChange,
	formattedMarketSymbol,
	stepSizeFromProps,
	disabledFromProps,
}: Params) {
	const [inputSource, setInputSource] = useSizeInputSource();
	const [showErrorMessage, setShowErrorMessage] = useState(false);
	const isConnected = useWalletIsConnected();

	const { value, disabled, stepSize, swapButtonLabel } = (() => {
		const isDisabled = !!(
			disabledFromProps ||
			(isConnected && maxQuoteAvailable != null && maxQuoteAvailable <= 0)
		);

		if (inputSource === 'base') {
			return {
				value: baseSizeStringValue,
				disabled: isDisabled,
				stepSize: stepSizeFromProps,
				swapButtonLabel: formattedMarketSymbol,
			};
		}

		return {
			value: quoteSizeStringValue,
			disabled: isDisabled,
			stepSize: undefined,
			swapButtonLabel: 'USDC',
		};
	})();

	const { handleChangeInputSource, handleChangeSize, handleBlur, handleFocus } =
		useSizeInputHandlers({
			inputSource,
			setInputSource,
			quoteSizeStringValue,
			onBaseSizeChange,
			onQuoteSizeChange,
			onInputSourceChange,
			setShowErrorMessage,
			baseSizeStringValue,
		});

	const id =
		inputSource === 'base'
			? INVARIANT_CHECKER_INPUT_IDS.baseSizeInput
			: INVARIANT_CHECKER_INPUT_IDS.quoteSizeInput;

	return {
		id,
		value,
		stepSize,
		disabled,
		inputSource,
		handleBlur,
		handleFocus,
		swapButtonLabel,
		handleChangeSize,
		handleChangeInputSource,
		showErrorMessage,
	};
}
